import SpeedometerIcon from "@uui/Speedometer03";
import { isEmpty } from "lodash/fp";

import { Skeleton, Typography } from "@mui/material";

import {
  CoachingModule,
  CoachingModuleContent,
  CoachingModuleEmptySummary,
  CoachingModuleHeader,
  CoachingModuleSummary,
  CoachingModuleTitle,
} from "@app/analytics/components/CoachingModule";
import ErrorState from "@app/analytics/components/ErrorState";
import { WpmGradientRange } from "@app/analytics/components/GradientRange/CoachingGradientRange";
import BenchmarkOverlay, {
  BenchmarkDescription,
  Benchmark,
} from "@app/analytics/MeetingReport/components/BenchmarkOverlay";
import Gauge from "@app/analytics/MeetingReport/components/Gauge";
import {
  WPM_GAUGE_LABELS,
  WPM_GAUGE_RANGE,
} from "@app/analytics/MeetingReport/components/Gauge/constants";
import Moments from "@app/analytics/MeetingReport/components/Moments";
import { Moment } from "@app/analytics/MeetingReport/components/Moments/types";
import { scaleWpmColor } from "@app/analytics/utils";
import COACHING_BENCHMARKS from "@app/common/coaching/coachingBenchmarks";
import { TOOLTIPS } from "@app/common/content";
import { pluralizePhrase } from "@app/common/utils";
import {
  getWpmSemanticScore,
  isWpmWithinRecommendedRange,
} from "@app/common/utils/coachingUtils";

import Stack from "@rui/Stack";

import useCoachingData from "./hooks/useCoachingData";
import InsufficientData from "./InsufficientData";
import { Description } from "./styles";

const getWpmDescription = (wpm: number | null) => {
  if (!wpm) return null;

  if (isWpmWithinRecommendedRange(wpm)) {
    return "Great job maintaining a comprehensible talking pace!";
  }

  return `Your talking speed is ${
    wpm < COACHING_BENCHMARKS.wpm.min ? "slower" : "faster"
  } than recommended.`;
};

const TalkingPaceSummary = ({
  isLoading,
  wpm,
}: {
  isLoading: boolean;
  wpm: number | null;
}) => {
  if (isLoading) {
    return <Skeleton width={100} />;
  }

  if (!wpm) {
    return (
      <CoachingModuleEmptySummary>
        Not enough talk time
      </CoachingModuleEmptySummary>
    );
  }

  const semanticScore = getWpmSemanticScore(wpm);

  return (
    <BenchmarkOverlay
      renderControl={({ onClick }) => (
        <CoachingModuleSummary icon={semanticScore} onClick={onClick}>
          {wpm} wpm
        </CoachingModuleSummary>
      )}
      title="Benchmark"
    >
      <BenchmarkDescription>
        For clarity and comprehension, aim for{" "}
        <Benchmark>
          {COACHING_BENCHMARKS.wpm.min}-{COACHING_BENCHMARKS.wpm.max} wpm
        </Benchmark>
        .
      </BenchmarkDescription>
      <Stack sx={{ py: 1 }}>
        <WpmGradientRange rate={wpm} withHighlightedRange withMarkerLabel />
      </Stack>
    </BenchmarkOverlay>
  );
};

const TalkingPaceModule = ({
  onClickMoment,
}: {
  onClickMoment: (m: Moment) => (() => void) | undefined;
}) => {
  const {
    isInitialLoading,
    talkingPace,
    insufficientTalkTimeData,
    isError,
    refetch,
  } = useCoachingData();

  const moments = talkingPace.moments;
  const wpm = talkingPace.rate;

  const hasMoments = !isEmpty(moments);

  return (
    <CoachingModule defaultExpanded expandable>
      <CoachingModuleHeader>
        <CoachingModuleTitle
          Icon={SpeedometerIcon}
          tooltip={TOOLTIPS.coaching.talkingPace}
        >
          Talking Pace
        </CoachingModuleTitle>
        <TalkingPaceSummary isLoading={isInitialLoading} wpm={wpm} />
      </CoachingModuleHeader>
      <CoachingModuleContent>
        {isError ? (
          <ErrorState refetch={refetch} sx={{ minHeight: "150px" }} />
        ) : insufficientTalkTimeData ? (
          <InsufficientData type="talk_time">
            Your talk time was too low to generate a talking pace.
          </InsufficientData>
        ) : (
          <Stack alignItems="center" spacing={3}>
            <Gauge
              height={150}
              labels={WPM_GAUGE_LABELS}
              loading={isInitialLoading}
              range={WPM_GAUGE_RANGE}
              subLabel="wpm"
              value={wpm}
              scaleColor={scaleWpmColor}
            />
            <Stack sx={{ width: "100%" }} spacing={1}>
              <Description>
                {getWpmDescription(wpm)}
                {hasMoments && (
                  <>
                    {" "}
                    Review{" "}
                    <Typography variant="bodySecondaryMed">
                      {pluralizePhrase(moments.length, "moment")}
                    </Typography>{" "}
                    where your wpm was outside the recommended range:
                  </>
                )}
              </Description>
              {hasMoments && (
                <Moments moments={moments} onClickMoment={onClickMoment} />
              )}
            </Stack>
          </Stack>
        )}
      </CoachingModuleContent>
    </CoachingModule>
  );
};

export default TalkingPaceModule;
